import { useAPI } from '../hooks/useAPI';
import type { WarehouseList } from '../types';
import { MetricCard } from './shared/MetricCard';
import { formatNumber, formatPercent, statusBadgeClass } from '../utils/format';

function utilizationTone(u: number): 'good' | 'warn' | 'bad' {
  if (u >= 0.9) return 'bad';
  if (u >= 0.75) return 'warn';
  return 'good';
}

export function WarehouseOps() {
  const { data, loading, error } = useAPI<WarehouseList>('/api/warehouse');

  if (loading) return <p className="text-slate-500">Loading warehouses…</p>;
  if (error) return <p className="text-rose-600">Failed to load: {error}</p>;
  if (!data) return null;

  const networkUtil =
    data.network.total_capacity > 0 ? data.network.total_used / data.network.total_capacity : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label="Warehouses" value={formatNumber(data.network.warehouses)} />
        <MetricCard label="Total Capacity" value={`${formatNumber(data.network.total_capacity)} u`} />
        <MetricCard
          label="Network Utilization"
          value={formatPercent(networkUtil)}
          tone={utilizationTone(networkUtil)}
        />
      </div>

      <section className="card overflow-x-auto">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Facilities</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="pb-2">Name</th>
              <th className="pb-2">Region</th>
              <th className="pb-2">Status</th>
              <th className="pb-2 text-right">Used / Capacity</th>
              <th className="pb-2 w-40">Utilization</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map((w) => (
              <tr key={w.id}>
                <td className="py-2 font-medium text-slate-800">{w.name}</td>
                <td className="py-2 text-slate-500">{w.region}</td>
                <td className="py-2">
                  <span className={`badge ${statusBadgeClass(w.status)}`}>{w.status}</span>
                </td>
                <td className="py-2 text-right text-slate-600">
                  {formatNumber(w.used_units)} / {formatNumber(w.capacity_units)}
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className={`h-full rounded-full ${
                          w.utilization >= 0.9
                            ? 'bg-rose-500'
                            : w.utilization >= 0.75
                              ? 'bg-amber-500'
                              : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(100, w.utilization * 100)}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-xs text-slate-500">
                      {formatPercent(w.utilization, 0)}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
